import { Maybe, type Result } from '@zaptic-external/ts-monads'
import { flatMap, uniqBy } from 'lodash'
import { useEffect, useState } from 'react'

export type PaginatedLoadingConfig<T extends { id: any }> = {
    initialPageNumber: number // this is useless scrolling won't work up only down
    initialNumberOfPagesToLoad?: number
    getItems: (pageNumber: number) => Promise<
        Result<
            string,
            {
                items: T[]
                totalPagesCount: number
            }
        >
    >
}

export const usePaginatedLoading = <T extends { id: any }>(config: PaginatedLoadingConfig<T>) => {
    const [items, setItems] = useState<T[]>([])
    const [pageToLoad, setPageToLoad] = useState(config.initialPageNumber)
    const [totalPagesCount, setTotalPagesCount] = useState(1)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(Maybe.nothing<string>())

    const loadNextPage = () => {
        const nextPageNumber = pageToLoad + 1
        if (nextPageNumber > totalPagesCount) {
            return
        }
        setPageToLoad(nextPageNumber)
        loadPage(nextPageNumber)
    }

    const reloadCurrentPage = () => {
        setError(Maybe.nothing())
        loadPage(pageToLoad)
    }

    const loadPage = async (pageNumber: number, { reset = false } = {}) => {
        setIsLoading(true)
        if (reset) {
            setItems([]) // Removes the previously rendered items from the DOM while getting items.
        }

        const itemsResult = await config.getItems(pageNumber)
        if (itemsResult.isOk()) {
            setItems(uniqBy([...(reset ? [] : items), ...itemsResult.value.items], 'id'))
            setTotalPagesCount(itemsResult.value.totalPagesCount)
            setError(Maybe.nothing())
        } else {
            setError(Maybe.just(itemsResult.error))
        }
        setIsLoading(false)
    }

    const loadPages = async ({ fromPage = 0, toPage }: { fromPage: number; toPage: number }) => {
        setIsLoading(true)
        const itemsResults = await Promise.all(
            [...Array(toPage - fromPage + 1).keys()]
                .map((index) => index + fromPage)
                .map((pageNumber) => config.getItems(pageNumber))
        )
        if (itemsResults.every((result) => result.isOk())) {
            const result = flatMap(itemsResults, (result: any) => result.value.items)
            setItems(uniqBy([...items, ...result], 'id'))
            setTotalPagesCount((itemsResults[0] as any).value.totalPagesCount)
            setError(Maybe.nothing())
        } else {
            setError(
                Maybe.just(
                    itemsResults
                        .map((result: any) => result.error)
                        .map(Boolean)
                        .join(', ')
                )
            )
        }
        setIsLoading(false)
    }

    const resetPaginatedLoading = () => {
        setPageToLoad(config.initialPageNumber)
        loadPage(config.initialPageNumber, { reset: true })
        setError(Maybe.nothing<string>())
    }

    const updateItems = (fn: (items: T[]) => T[]) => setItems(fn(items))

    useEffect(() => {
        const toPage = config.initialPageNumber + (config.initialNumberOfPagesToLoad ?? 0)
        loadPages({
            fromPage: config.initialPageNumber,
            toPage,
        })
        setPageToLoad(toPage)
    }, [])

    return {
        items,
        currentPageNumber: pageToLoad,
        isLoading,
        error,
        loadNextPage,
        setPageToLoad,
        reloadCurrentPage,
        resetPaginatedLoading,
        updateItems,
        hasNextPage: pageToLoad < totalPagesCount,
    }
}
